import React from 'react';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { trackEvent } from '@medflex/utils/Tracking';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import InputField from '@medflex/components/stories/InputField';
import { SmsSenderName } from '@medflex/shared-constants';
import Button from '@medflex/components/stories/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MODALS } from '../hooks/useIsModal';
import useModal from '../hooks/useModal';
import useUserProfile from '../hooks/useUserProfile';

const ALPHA_NUMERIC_AND_SPACE_REGEX = /^[a-zA-Z0-9\s]*$/;

const SmsSettingsEditModal = () => {
  const { t } = useTranslation('physician');
  const { close } = useModal();
  const { userProfile, updateSmsSettings } = useUserProfile();
  const { senderName } = userProfile.attributes.notificationSettings.sms || '';

  const {
    register,
    handleSubmit: wrapSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .trim()
          .max(SmsSenderName.MAX_LENGTH)
          .matches(ALPHA_NUMERIC_AND_SPACE_REGEX),
      }),
    ),
  });

  const handleSubmit = async (data, event) => {
    const { name } = data;

    try {
      await updateSmsSettings({ senderName: name.trim() || null });
      showSuccessToast(t('toasts.smsSettings.senderName.success.title'), null, {
        toastId: 'smsSettingsChangeSuccess',
      });
      trackEvent(event, { actionText: 'manage_Textblock' });
    } catch (e) {
      showAlertToast(t('toasts.smsSettings.senderName.alert.title'), null, {
        toastId: 'smsSettingsChangeFailed',
      });
    }

    close({ modal: MODALS.smsSettingsEdit });
  };

  return (
    <Modal
      showCloseButton
      onClickCloseButton={() => close({ modal: MODALS.smsSettingsEdit })}
      title={t('modals.SmsSettingsEditModal.title')}
    >
      <div data-component="SmsSettingsEditModal">
        <p className="mb-4 text-slate-600">{t('modals.SmsSettingsEditModal.description')}</p>
        <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-1 flex-col">
          <InputField
            id="name"
            name="name"
            label={t('modals.SmsSettingsEditModal.label')}
            ref={register}
            placeholder={t('modals.SmsSettingsEditModal.placeholder')}
            state={isValid ? 'default' : 'invalid'}
            defaultValue={senderName}
            className="mb-1"
          />
          <span className={`text-sm mb-4 ${!isValid ? 'text-red-500' : 'text-slate-500'}`}>
            {t('modals.SmsSettingsEditModal.hint')}
          </span>
          <Button type="submit" className="self-end" isDisabled={!isValid}>
            {t('modals.SmsSettingsEditModal.cta')}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default SmsSettingsEditModal;
